/**
 * Cartesian pose:
 * Represents a position and orientation in 6-dimensional cartesian space (3 dimensions
 * for position, 3 dimensions for orientation).
 *
 * Rather than using 3 numbers (roll, pitch, yaw) for orientation, we use 4 numbers
 * (a quaternion) which has a number of computational benefits.
 *
 * This is a slightly unique packing format; squashes the position and orientation as a Vector3
 * and quaternion.
 *
 * It calls the Vector3's components <x y z>
 * and the quaternion's components <w i j k> (as opposed to <w x y z> as is more typical)
 */
import { sum } from 'lodash';
import { Vector3, Quaternion as ThreeQuaternion, Matrix4, Euler } from 'three';
import * as zod from 'zod';

import {
  CartesianPosition,
  interpolateCartesianPositions,
} from './CartesianPosition';
import {
  CommonQuaternions,
  quaternionInverse,
  quaternionMultiply,
  rotateVectorByQuaternion,
  slerpQuaternions,
  UnitQuaternion,
} from './Quaternion';
import type { Quaternion } from './Quaternion';

export const CartesianPose = zod.intersection(
  UnitQuaternion,
  CartesianPosition,
);

export type CartesianPose = zod.infer<typeof CartesianPose>;

const unit = new Vector3(1, 1, 1);

export const ZERO_ROTATION = UnitQuaternion.parse({
  i: 0,
  j: 0,
  k: 0,
  w: 1,
});

export const ZERO_OFFSET = {
  x: 0,
  y: 0,
  z: 0,
};

export const ZERO_POSE: CartesianPose = {
  ...ZERO_ROTATION,
  ...ZERO_OFFSET,
};

export function cartesianPoseToMatrix4(pose: CartesianPose): Matrix4 {
  const quaternion = new ThreeQuaternion(pose.i, pose.j, pose.k, pose.w);
  const position = new Vector3(pose.x, pose.y, pose.z);

  return new Matrix4().compose(position, quaternion, unit);
}

export function matrix4ToCartesianPose(matrix: Matrix4): CartesianPose {
  const translation = new Vector3();
  const rotation = new ThreeQuaternion();

  translation.setFromMatrixPosition(matrix);
  rotation.setFromRotationMatrix(matrix);
  // rotation.normalize();

  return {
    i: rotation.x,
    j: rotation.y,
    k: rotation.z,
    w: rotation.w,
    ...translation,
  };
}

/**
 * applyCompoundPose
 * Apply cartesian transformation to cartesian pose.
 */
export function applyCompoundPose(
  pose: CartesianPose,
  transform: CartesianPose,
): CartesianPose {
  const poseMatrix = cartesianPoseToMatrix4(pose);
  const transformMatrix = cartesianPoseToMatrix4(transform);

  const appliedMatrix = transformMatrix.multiply(poseMatrix);

  const translation = new Vector3();
  const rotation = new ThreeQuaternion();

  translation.setFromMatrixPosition(appliedMatrix);
  rotation.setFromRotationMatrix(appliedMatrix);

  return {
    i: rotation.x,
    j: rotation.y,
    k: rotation.z,
    w: rotation.w,
    ...translation,
  };
}

/**
 * applyRelativeOffset
 * Apply relative offset to cartesian pose without coupling rotation and translation.
 * This prevents rotational offsets from causing unwanted positional changes.
 *
 * @param pose - The base pose to apply the offset to
 * @param offset - The offset to apply (position and/or rotation)
 * @returns The resulting pose with the offset applied
 */
export function applyRelativeOffset(
  pose: CartesianPose,
  offset: CartesianPose,
): CartesianPose {
  // Apply positional offset directly (additive)
  const newPosition = {
    x: (pose.x ?? 0) + (offset.x ?? 0),
    y: (pose.y ?? 0) + (offset.y ?? 0),
    z: (pose.z ?? 0) + (offset.z ?? 0),
  };

  // Apply rotational offset by quaternion multiplication
  const poseQuat = new ThreeQuaternion(
    pose.i ?? 0,
    pose.j ?? 0,
    pose.k ?? 0,
    pose.w ?? 1,
  );

  const offsetQuat = new ThreeQuaternion(
    offset.i ?? 0,
    offset.j ?? 0,
    offset.k ?? 0,
    offset.w ?? 1,
  );

  // Multiply quaternions: offset * pose (apply offset to pose)
  const newQuat = offsetQuat.multiply(poseQuat);
  newQuat.normalize();

  return {
    ...newPosition,
    i: newQuat.x,
    j: newQuat.y,
    k: newQuat.z,
    w: newQuat.w,
  };
}

const firstVec = new Vector3(0, 0, 0);
const secondVec = new Vector3(0, 0, 0);

export function posesAreEqual(
  first: CartesianPose,
  second: CartesianPose,
): boolean {
  firstVec.x = first.x;
  firstVec.y = first.y;
  firstVec.z = first.z;

  secondVec.x = second.x;
  secondVec.y = second.y;
  secondVec.z = second.z;

  return firstVec.equals(secondVec);
}

export function distanceBetweenPoses(
  first: CartesianPose,
  second: CartesianPose,
): number {
  firstVec.x = first.x;
  firstVec.y = first.y;
  firstVec.z = first.z;

  secondVec.x = second.x;
  secondVec.y = second.y;
  secondVec.z = second.z;

  return firstVec.distanceTo(secondVec);
}

export function differenceBetweenPoses(
  first: CartesianPose,
  second: CartesianPose,
): CartesianPose {
  const firstMatrix = cartesianPoseToMatrix4(first);
  const secondMatrix = cartesianPoseToMatrix4(second);

  const differenceMatrix = secondMatrix.multiply(firstMatrix.invert());

  return matrix4ToCartesianPose(differenceMatrix);
}

export function additionBetweenPoses(
  first: CartesianPose,
  second: CartesianPose,
): CartesianPose {
  const firstMatrix = cartesianPoseToMatrix4(first);
  const secondMatrix = cartesianPoseToMatrix4(second);

  const sumMatrix = firstMatrix.multiply(secondMatrix);

  return matrix4ToCartesianPose(sumMatrix);
}

export function averagePositions(
  positions: CartesianPosition[],
  weights: number[] | null = null,
  reverseWeights: boolean = false,
): CartesianPosition {
  if (positions.length === 0) {
    throw new Error('Cannot get average position of 0 positions');
  }

  const newWeights = weights || positions.map(() => 1); // can't modify param

  if (reverseWeights) {
    for (let i = 0; i < newWeights.length; i += 1) {
      if (newWeights[i] === 0) {
        // avoid divide by zero
        return positions[i]; // if any weight is 0 then we can just return that position
      }

      newWeights[i] = 1 / newWeights[i];
    }
  }

  // normalize weights
  const totalWeight = sum(newWeights);
  const normalWeights = newWeights.map((weight) => weight / totalWeight);

  const averagePosition: CartesianPosition = {
    x: 0,
    y: 0,
    z: 0,
  };

  for (let i = 0; i < positions.length; i += 1) {
    averagePosition.x += positions[i].x * normalWeights[i];
    averagePosition.y += positions[i].y * normalWeights[i];
    averagePosition.z += positions[i].z * normalWeights[i];
  }

  return averagePosition;
}

export function invertPose(pose: CartesianPose): CartesianPose {
  return matrix4ToCartesianPose(cartesianPoseToMatrix4(pose).invert());
}

// this function is going between 2 different tooltip
// coordinate systems that have different axes orientations,
// but the same origin (the tooltip).
// "Base coordinates" here mean the axes are aligned with the
// the base's axes: Z-up, Y-forward, X-left).
// "Tooltip coordinates" here mean the axes are Y-up, Z-forward, X-right.
export function tooltipCoordinatesToBaseCoordinates(
  pose: CartesianPose,
): CartesianPose {
  return {
    i: pose.k,
    j: pose.i,
    k: pose.j,
    w: pose.w,
    x: pose.z,
    y: pose.x,
    z: pose.y,
  };
}

// Convert a pose expressed in base coordinates into ROS2 tooltip coordinate system
export function baseCoordinatesToTooltip(pose: CartesianPose): CartesianPose {
  return {
    x: pose.y,
    y: pose.z,
    z: pose.x,
    i: pose.j,
    j: pose.k,
    k: pose.i,
    w: pose.w,
  };
}

/**
 * Converts a pose from ROS tool0 frame to Three.js frame.
 *
 * The frame transformation corresponds to:
 * - Rotate +90° about X
 * - Then rotate 180° about Z
 *
 *
 * @param pose - Pose in ROS tool0 coordinate frame
 * @returns Pose in Three.js coordinate frame
 */
export function rosTool0ToThreeJS(pose: CartesianPose): CartesianPose {
  // Frame transform quaternion: rotate +90° about X, then 180° about Z
  const frameQuaternion = new ThreeQuaternion().setFromEuler(
    new Euler(Math.PI / 2, 0, Math.PI, 'XYZ'),
  );

  // Orientation: q' = F * q * F⁻¹ (quaternion conjugation)
  const poseQuaternion = new ThreeQuaternion(pose.i, pose.j, pose.k, pose.w);
  const frameQuaternionInv = frameQuaternion.clone().invert();

  const transformedQuaternion = frameQuaternion
    .clone()
    .multiply(poseQuaternion)
    .multiply(frameQuaternionInv);

  // Position: Transform using quaternion (equivalent to manual remap but faster)
  const transformedPosition = new Vector3(pose.x, pose.y, pose.z);
  transformedPosition.applyQuaternion(frameQuaternion);

  return {
    x: transformedPosition.x,
    y: transformedPosition.y,
    z: transformedPosition.z,
    i: transformedQuaternion.x,
    j: transformedQuaternion.y,
    k: transformedQuaternion.z,
    w: transformedQuaternion.w,
  };
}

export function cartesianPositionFromPose(
  pose: CartesianPose,
): CartesianPosition {
  return {
    x: pose.x,
    y: pose.y,
    z: pose.z,
  };
}

/**
 * Converts target pose to ROS2 tooltip pose.  The target poses such as returned
 * by RoutineContext `getTargetPose` give a pose in with the X-axis pointing out in
 * the direction of the tooltip, and Z-axis pointing up.  The ROS2 tooltip pose follows
 * the more common convention of Z-axis pointing out in the direction of the tooltip
 * @param target
 * @returns tooltip pose
 */
export function targetToROS2Tooltip(targetPose: CartesianPose): CartesianPose {
  const quaternion = new ThreeQuaternion(
    targetPose.i,
    targetPose.j,
    targetPose.k,
    targetPose.w,
  );

  quaternion.multiply(CommonQuaternions.ROTATE_XY_90);

  return {
    ...targetPose,
    i: quaternion.x,
    j: quaternion.y,
    k: quaternion.z,
    w: quaternion.w,
  };
}

/**
 * Converts ROS2 tooltip pose back to target pose. This is the inverse of targetToROS2Tooltip.
 * ROS2 tooltip poses use Z-axis pointing out in the direction of the tooltip,
 * while target poses use X-axis pointing out with Z-axis pointing up.
 * @param tooltipPose ROS2 tooltip pose
 * @returns target pose
 */
export function ros2TooltipToTarget(tooltipPose: CartesianPose): CartesianPose {
  const quaternion = new ThreeQuaternion(
    tooltipPose.i,
    tooltipPose.j,
    tooltipPose.k,
    tooltipPose.w,
  );

  // To invert the rotation, multiply by inverse (negative 90 degrees)
  quaternion.multiply(CommonQuaternions.ROTATE_XY_90.clone().invert());

  return {
    ...tooltipPose,
    i: quaternion.x,
    j: quaternion.y,
    k: quaternion.z,
    w: quaternion.w,
  };
}

// Change of basis for a pose expressed in ROS tool0 into a tip_link frame
// For position: v_tip = R_tip^T * v_tool0
// For orientation: q_tip_delta = q_tip^{-1} ⊗ q_tool0_delta ⊗ q_tip
export function rosTool0ToRosTooltip(
  tool0Pose: CartesianPose,
  tipOrientationFromTool0: Quaternion,
): CartesianPose {
  const qTipInv = quaternionInverse(tipOrientationFromTool0);

  // position
  const pTool0 = { x: tool0Pose.x, y: tool0Pose.y, z: tool0Pose.z };
  const pTip = rotateVectorByQuaternion(pTool0, qTipInv);

  // orientation
  const qTool0 = {
    i: tool0Pose.i,
    j: tool0Pose.j,
    k: tool0Pose.k,
    w: tool0Pose.w,
  };

  const qStep = quaternionMultiply(qTipInv, qTool0);
  const qTip = quaternionMultiply(qStep, tipOrientationFromTool0);

  return {
    x: pTip.x,
    y: pTip.y,
    z: pTip.z,
    i: qTip.i,
    j: qTip.j,
    k: qTip.k,
    w: qTip.w,
  };
}

// Linear interpolation of position and spherical linear interpolation of orientation between two poses
// by a given fraction
export function interpolateCartesianPoses(
  start: CartesianPose,
  end: CartesianPose,
  fraction: number,
): CartesianPose {
  return {
    // linear interpolation of cartesian position
    ...interpolateCartesianPositions(start, end, fraction),
    // spherical linear interpolation of orientation
    ...slerpQuaternions(start, end, fraction),
  };
}
